/** Route Map screen — shows a single route with stops highlighted, route line, and live vehicles. */
import { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import Map, { Marker, Popup, NavigationControl, Source, Layer, type MapRef } from 'react-map-gl/maplibre';
import 'maplibre-gl/dist/maplibre-gl.css';
import { getDepartures, type Departure } from '@/services/departures';
import { planTrip, type JourneyLeg } from '@/services/trip-planner';
import { searchStops } from '@/services/stop-search';
import { fetchVehiclePositions, type RealtimeVehiclePosition, type RealtimeMode } from '@/services/gtfs-realtime';
import { fetchTripDelays } from '@/services/trip-updates';
import { formatRouteLabel, formatDelayText, routeIdMatches } from '@/services/route-labels';
import { DARK_MAP_STYLE } from '@/services/map-style';
import { findStaticRoutePattern, inferVehicleBearing } from '@/services/route-patterns';
import { VehicleMarker, vehicleDisplayLabel } from '@/components/VehicleMarker';
import { RefreshCountdown } from '@/components/RefreshCountdown';
import { canPoll } from '@/services/polling';

const MODE_COLORS: Record<string, string> = {
  Train: '#F6891F', Bus: '#0072C6', Ferry: '#61B255', Metro: '#0099CD', 'Light Rail': '#D81E34',
};
const BADGE_LETTER: Record<string, string> = {
  Train: 'T', Bus: 'B', Ferry: 'F', Metro: 'M', 'Light Rail': 'L',
};
const MODE_TO_RT: Record<string, RealtimeMode> = {
  Train: 'sydneytrains', Bus: 'buses', Ferry: 'ferries', Metro: 'metro', 'Light Rail': 'lightrail',
};

type RouteVehicle = RealtimeVehiclePosition & { delay?: number; delayLoaded?: boolean };

function endpointQueries(name: string, mode?: string): string[] {
  const withoutVia = name.split(/\s+via\s+/i)[0].trim();
  const isRail = mode === 'Train' || mode === 'Metro' || mode === 'Light Rail';
  if (/^Central Station.*Pitt St/i.test(withoutVia)) {
    return ['Pitt St at Barlow St, Haymarket', withoutVia];
  }
  const stationMatch = withoutVia.match(/^(.+?\bStation)\b(.*)$/i);
  const queries = [withoutVia];
  if (stationMatch) {
    queries.unshift(stationMatch[1]);
    const detail = stationMatch[2].replace(/^[,\s]+/, '').trim();
    if (detail) queries.unshift(`${stationMatch[1]}, ${detail}, Sydney`);
  } else if (/^[\p{L}\s'-]+$/u.test(withoutVia)) {
    // Only rail/metro destinations should be searched as "X Station" — bus
    // destinations like "City Hyde Park" would otherwise match an unrelated
    // rail station (Macquarie Park) instead of the actual bus stop.
    if (isRail) queries.unshift(`${withoutVia} Station`);
    else queries.unshift(`${withoutVia}, Sydney`);
  }
  return [...new Set(queries.filter(Boolean))];
}

function sydneyLocalTime(iso: string): string {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Australia/Sydney', year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23',
  }).formatToParts(new Date(iso));
  const value = (type: Intl.DateTimeFormatPartTypes) => parts.find(p => p.type === type)?.value || '';
  return `${value('year')}-${value('month')}-${value('day')}T${value('hour')}:${value('minute')}:${value('second')}`;
}

async function resolveEndpoint(name: string, excludedId?: string, mode?: string) {
  const isRail = mode === 'Train' || mode === 'Metro' || mode === 'Light Rail';
  for (const query of endpointQueries(name, mode)) {
    const results = await searchStops(query);
    // For rail/metro legs the destination must be a station, not a nearby bus
    // stop ("Sydenham" resolves to "Sydenham Rd at Pittwater Rd" first).
    // Prefer stops whose name ends in "Station" or that carry a rail mode.
    // For bus/ferry legs, prefer the best-scored stop (which is usually the
    // actual bus stop / wharf, not an unrelated rail station).
    const railStop = results.find(s => s.id !== excludedId && /station|wharf|interchange|light rail$/i.test(s.name.trim()));
    const fallback = results.find(s => s.id !== excludedId);
    if (isRail && railStop) return railStop;
    if (fallback) return fallback;
  }
  return undefined;
}

async function loadFullRouteLeg(match: Departure, routeNumber: string, stopId?: string): Promise<JourneyLeg | undefined> {
  if (!match.origin || !match.destination) return undefined;
  const origin = await resolveEndpoint(match.origin, undefined, match.mode);
  if (!origin) return undefined;
  const destination = await resolveEndpoint(match.destination, origin.id, match.mode);
  const time = sydneyLocalTime(match.scheduledTime);
  const options = { routeShortName: routeNumber, resultCount: 10, addAccessLeg: false } as const;
  const candidateLegs: JourneyLeg[] = [];

  const collect = async (fromId: string, toId: string, extra?: { destinationType?: string }) => {
    const journeys = await planTrip(fromId, toId, time, true, undefined, extra ? { ...options, ...extra } : options);
    candidateLegs.push(...journeys.flatMap(j => j.legs));
  };

  // The trip planner splits long routes, so a single origin→destination plan
  // only returns the sub-portion near the stop. Plan both halves through the
  // user's stop — origin → stop and stop → destination — so the returned legs
  // together carry times for the full route.
  if (stopId && destination) {
    await collect(origin.id, stopId);
    await collect(stopId, destination.id);
  } else if (destination) {
    await collect(origin.id, destination.id);
  }

  const hasExactRoute = candidateLegs.some(leg => routeNumberMatches(leg.routeShortName, routeNumber));
  if (!hasExactRoute) {
    for (const destinationQuery of endpointQueries(match.destination, match.mode).slice(0, 1)) {
      await collect(origin.id, destinationQuery, { destinationType: 'any' });
      if (stopId) await collect(stopId, destinationQuery, { destinationType: 'any' });
    }
  }

  const matched = candidateLegs
    .filter(leg => leg.mode !== 'walk' && leg.modeName === match.mode && routeNumberMatches(leg.routeShortName, routeNumber))
    .sort((a, b) => (b.stops?.length || 0) - (a.stops?.length || 0));
  const best = matched[0];
  if (!best) return undefined;

  // Combine every matched leg's per-stop times (both halves) into one stop
  // list so the full route carries arrival/departure times at every stop.
  const combined = new globalThis.Map<string, NonNullable<JourneyLeg['stops']>[number]>();
  for (const leg of matched) {
    for (const s of leg.stops || []) {
      const existing = combined.get(s.id);
      if (!existing) combined.set(s.id, s);
      else if (s.departure && !existing.departure) combined.set(s.id, { ...existing, departure: s.departure });
      else if (s.arrival && !existing.arrival) combined.set(s.id, { ...existing, arrival: s.arrival });
    }
  }
  return { ...best, stops: [...combined.values()] };
}

/**
 * Route matching helper — TfNSW reports metro/bus numbers inconsistently:
 * the departure monitor may say "M1" while the trip planner reports the full
 * line name ("M1 Metro North West & Bankstown Line"). Match on the route
 * number prefix so both forms resolve to the same service.
 */
function routeNumberMatches(actual: string | undefined, expected: string): boolean {
  if (!actual) return false;
  const a = actual.trim().toLocaleUpperCase();
  const e = expected.trim().toLocaleUpperCase();
  if (a === e) return true;
  // "M1 Metro North West & Bankstown Line" starts with "M1"
  if (a.startsWith(e + ' ')) return true;
  // "M1" could also appear with a suffix like "M1-1" or "T2-1"
  return a.startsWith(e + '-') || a === e.split(/\s+/)[0];
}

function formatDelay(seconds: number): { text: string; cls: string } {
  const min = Math.round(seconds / 60);
  if (min > 0) return { text: `${min} min late`, cls: 'delay-late' };
  if (min < 0) return { text: `${Math.abs(min)} min early`, cls: 'delay-early' };
  return { text: 'On time', cls: 'delay-ontime' };
}

/** Merge static pattern stops with the departure's own service-stop times. */
function mergeServiceTimes(
  staticStops: { id: string; name: string; coords: [number, number] }[],
  serviceStops: Departure['serviceStops'],
): { name: string; id: string; coords?: [number, number]; depTime?: string; arrTime?: string }[] {
  if (!serviceStops?.length) return staticStops.map(s => ({ ...s }));
  const byId = new globalThis.Map(serviceStops.map(s => [s.id, s]));
  return staticStops.map(s => {
    const live = byId.get(s.id);
    return live ? { ...s, depTime: live.departure, arrTime: live.arrival } : { ...s };
  });
}

/**
 * Merge trip-planner leg stop times (arrival/departure) into static pattern
 * stops, matched by stop id. Used when the departure monitor doesn't carry
 * per-stop times (e.g. metro trips expose no previous/onward locations).
 */
function mergeLegTimes(
  staticStops: { name: string; id: string; coords?: [number, number]; depTime?: string; arrTime?: string }[],
  leg: JourneyLeg | undefined,
): { name: string; id: string; coords?: [number, number]; depTime?: string; arrTime?: string }[] {
  if (!leg?.stops?.length) return staticStops;
  const byId = new globalThis.Map(leg.stops.map(s => [s.id, s]));
  const edgeTimes = new globalThis.Map<string, { dep?: string; arr?: string }>();
  if (leg.origin?.id) edgeTimes.set(leg.origin.id, { dep: leg.origin.departure });
  if (leg.destination?.id) edgeTimes.set(leg.destination.id, { arr: leg.destination.arrival });
  return staticStops.map(s => {
    const live = byId.get(s.id);
    const edge = edgeTimes.get(s.id);
    return {
      ...s,
      depTime: live?.departure || edge?.dep || s.depTime,
      arrTime: live?.arrival || edge?.arr || s.arrTime,
    };
  });
}

export function RouteMapScreen() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const tripId = searchParams.get('trip');
  const stopId = searchParams.get('stop');
  const modeParam = searchParams.get('mode') || '';
  const routeParam = searchParams.get('route') || '';
  const timeParam = searchParams.get('time');
  const destinationParam = searchParams.get('dest');
  const [dep, setDep] = useState<Departure | null>(null);
  const [routeStops, setRouteStops] = useState<{ name: string; id: string; coords?: [number, number]; depTime?: string; arrTime?: string }[]>([]);
  const [routeColor, setRouteColor] = useState('#F6891F');
  const [loading, setLoading] = useState(true);
  const [mapCenter, setMapCenter] = useState<[number, number]>([151.2093, -33.8688]);
  const [vehicles, setVehicles] = useState<RouteVehicle[]>([]);
  const [lastVehicleUpdate, setLastVehicleUpdate] = useState<Date | null>(null);
  const [selectedVehicle, setSelectedVehicle] = useState<RouteVehicle | null>(null);
  const [selectedStop, setSelectedStop] = useState<{ id: string; name: string; coords: [number, number] } | null>(null);
  const currentStopRowRef = useRef<HTMLElement | null>(null);
  const mapRef = useRef<MapRef | null>(null);
  const stopRowRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const vehicleIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const rtMode = modeParam ? MODE_TO_RT[modeParam] : undefined;

  // Load route stops
  const [legPath, setLegPath] = useState<[number, number][] | undefined>(undefined);
  useEffect(() => {
    if (!stopId) { setLoading(false); return; }
    (async () => {
      setLoading(true);
      try {
        // 0. Look up the stop's own coordinates as a reliable fallback for centering
        let requestedStopCoords: [number, number] | undefined;
        let requestedStopName = '';
        try {
          const stopResults = await searchStops(stopId);
          if (stopResults[0]?.latitude && stopResults[0]?.longitude) {
            requestedStopCoords = [stopResults[0].longitude, stopResults[0].latitude];
            requestedStopName = stopResults[0].name;
            setMapCenter(requestedStopCoords);
          }
        } catch (e) { console.warn('RouteMap: failed to look up stop coords for center:', e); }

        // 1. Find the departure — try exact tripId match, then routeNumber fallback
        const scheduledParam = timeParam;
        const localScheduled = scheduledParam ? sydneyLocalTime(scheduledParam) : '';
        const datedDeps = scheduledParam ? await getDepartures(stopId, 100, undefined, {
          itdDate: localScheduled.slice(0, 10).replace(/-/g, ''),
          itdTime: localScheduled.slice(11, 16).replace(':', ''),
        }) : [];
        const currentDeps = datedDeps.length > 0 ? [] : await getDepartures(stopId, 100);
        const serviceDeps = [...datedDeps, ...currentDeps];
        let selectedDeparture = serviceDeps.find(d => d.scheduledTime === scheduledParam && d.routeNumber === routeParam);
        if (!selectedDeparture) selectedDeparture = serviceDeps.find(d => d.tripId === tripId && (!destinationParam || d.destination === destinationParam));
        if (!selectedDeparture) selectedDeparture = serviceDeps.find(d => d.routeNumber === routeParam && (!destinationParam || d.destination === destinationParam));
        if (!selectedDeparture) selectedDeparture = serviceDeps.find(d => d.routeNumber === routeParam) || serviceDeps[0];
        setDep(selectedDeparture);
        if (selectedDeparture?.stopCoords) setMapCenter(selectedDeparture.stopCoords);
        setRouteColor(MODE_COLORS[selectedDeparture?.mode || ''] || '#F6891F');

        // Static GTFS is authoritative and avoids waiting for a realtime request
        // that can omit an individual service or temporarily run slowly.
        if (routeParam && destinationParam) {
          const staticPattern = await findStaticRoutePattern(routeParam, requestedStopName, destinationParam, requestedStopCoords);
          if (staticPattern) {
            const staticStops = staticPattern.stops.map(stop => ({ id: stop.id, name: stop.name, coords: [stop.lon, stop.lat] as [number, number] }));
            let merged = mergeServiceTimes(staticStops, selectedDeparture?.serviceStops);
            // The departure monitor's serviceStops only covers a partial
            // portion of the route (the stops near the current stop), so
            // "hasNoTimes" is false even though most stops lack times. Always
            // backfill from the trip planner — mergeLegTimes preserves any
            // existing times and fills in the rest of the route.
            if (selectedDeparture?.origin && selectedDeparture.destination) {
              const leg = await loadFullRouteLeg(selectedDeparture, routeParam, stopId);
              merged = mergeLegTimes(merged, leg);
            }
            setRouteStops(merged);
            setLegPath(staticPattern.path?.length ? staticPattern.path : staticStops.map(stop => stop.coords));
            setRouteColor(MODE_COLORS[modeParam || ''] || '#F6891F');
            return;
          }
        }

        if (selectedDeparture) {
          const staticPattern = await findStaticRoutePattern(
            routeParam || selectedDeparture.routeNumber,
            selectedDeparture.origin || '',
            selectedDeparture.destination,
            selectedDeparture.stopCoords,
          );
          if (staticPattern) {
            const staticStops = staticPattern.stops.map(stop => ({
              id: stop.id, name: stop.name, coords: [stop.lon, stop.lat] as [number, number],
            }));
            // Merge the departure's own service-stop times (previous/onward
            // locations) so the stop list shows real times for this run.
            let merged = mergeServiceTimes(staticStops, selectedDeparture.serviceStops);
            // Always backfill from the trip planner so the full route carries
            // times, not just the partial portion near the current stop.
            if (selectedDeparture.origin && selectedDeparture.destination) {
              const leg = await loadFullRouteLeg(selectedDeparture, routeParam || selectedDeparture.routeNumber, stopId);
              merged = mergeLegTimes(merged, leg);
            }
            setRouteStops(merged);
            setLegPath(staticPattern.path?.length ? staticPattern.path : staticStops.map(stop => stop.coords));
            return;
          }
          const fullLeg = await loadFullRouteLeg(selectedDeparture, routeParam || selectedDeparture.routeNumber, stopId);
          if (fullLeg) {
            const fullStops = [
              { name: fullLeg.origin.name, id: fullLeg.origin.id, coords: fullLeg.origin.coords, depTime: fullLeg.origin.departure, arrTime: undefined },
              ...(fullLeg.stops || []).map(s => ({ name: s.name, id: s.id, coords: s.coords, depTime: s.departure, arrTime: s.arrival })),
              { name: fullLeg.destination.name, id: fullLeg.destination.id, coords: fullLeg.destination.coords, depTime: undefined, arrTime: fullLeg.destination.arrival },
            ].filter((s, i, all) => all.findIndex(x => x.id === s.id) === i);
            setRouteStops(fullStops);
            setLegPath(fullLeg.path?.length ? fullLeg.path : undefined);
          } else {
            setRouteStops([]);
            setLegPath(undefined);
          }
        }
        // Legacy fallback retained temporarily for reference but never selected.
        if (Date.now() < 0) {
        const deps = await getDepartures(stopId, 100);
        let match = deps.find(d => d.tripId === tripId);
        if (!match && routeParam) {
          match = deps.find(d => d.routeNumber === routeParam);
        }
        if (!match) match = deps[0];
        setDep(match);

        if (match?.serviceStops && match.serviceStops.length > 1) {
          let serviceRoute = match.serviceStops.map(s => ({
            name: s.name, id: s.id, coords: s.coords, depTime: s.departure, arrTime: s.arrival,
          }));

          // onwardLocations starts at the selected stop. When the API supplies the
          // service origin, prepend the matching inbound leg so the whole run is shown.
          if (match.origin && serviceRoute[0]?.id === stopId) {
            try {
              const originCandidates = await searchStops(match.origin.split(',')[0].trim());
              const routeOrigin = originCandidates.find(s => s.id !== stopId);
              if (routeOrigin) {
                const inbound = await planTrip(routeOrigin.id, stopId, match.scheduledTime, false);
                const inboundLeg = inbound.flatMap(j => j.legs).find(l =>
                  l.modeName === match.mode && l.mode !== 'walk' &&
                  (!routeParam || l.routeShortName?.toLocaleUpperCase() === routeParam.toLocaleUpperCase()),
                );
                if (inboundLeg) {
                  const preceding = [
                    { name: inboundLeg.origin.name, id: inboundLeg.origin.id, coords: inboundLeg.origin.coords, depTime: inboundLeg.origin.departure, arrTime: undefined },
                    ...(inboundLeg.stops || []).map(s => ({ name: s.name, id: s.id, coords: s.coords, depTime: s.departure, arrTime: s.arrival })),
                  ].filter(s => s.id !== stopId);
                  serviceRoute = [...preceding, ...serviceRoute].filter((s, i, all) => all.findIndex(x => x.id === s.id) === i);
                  if (inboundLeg.path?.length) setLegPath(inboundLeg.path);
                }
              }
            } catch (e) { console.warn('RouteMap: could not load stops before current stop:', e); }
          }
          setRouteStops(serviceRoute);
        }

        if (match?.stopCoords) {
          setMapCenter([match.stopCoords[0], match.stopCoords[1]]);
        }
        setRouteColor(MODE_COLORS[match?.mode || ''] || '#F6891F');

        // 2. Try to find destination stop ID from the destination name
        if (match?.destination && (!match.serviceStops || match.serviceStops.length <= 1)) {
          try {
            // Progressive fallbacks for destination resolution
            const candidates: string[] = [];
            const viaSplit = match.destination.split(' via ')[0].trim();
            const commaSplit = viaSplit.split(',')[0].trim();
            const parenSplit = commaSplit.split('(')[0].trim();
            candidates.push(viaSplit, commaSplit, parenSplit);
            for (const cand of [...new Set(candidates)]) {
              if (!cand) continue;
              const results = await searchStops(cand);
              const destStop = results.find(r => r.id !== stopId) || results[0];
              if (destStop?.id) {
                const journeys = await planTrip(stopId, destStop.id);
                // Filter by modeName matching the departure's mode to avoid picking train legs for bus routes
                const targetMode = match?.mode || '';
                const transitLeg = journeys.flatMap(j => j.legs)
                  .find(l => l.mode !== 'walk' && l.modeName === targetMode && (l.stops?.length || l.origin.coords))
                  || journeys.flatMap(j => j.legs).find(l => l.mode !== 'walk' && (l.stops?.length || l.origin.coords));
                if (transitLeg?.stops && transitLeg.stops.length > 0) {
                  const stopSeq = transitLeg.stops;
                  // Deduplicate by stop ID — stopSequence sometimes echoes origin/destination
                  const seen = new Set<string>();
                  const dedup = (s: { name: string; id: string }) => { if (seen.has(s.id)) return false; seen.add(s.id); return true; };
                  const allStops: { name: string; id: string; coords?: [number, number]; depTime?: string; arrTime?: string }[] = [
                    { name: transitLeg.origin.name, id: transitLeg.origin.id, coords: transitLeg.origin.coords, depTime: transitLeg.origin.departure },
                    ...stopSeq.filter(dedup).map(s => ({ name: s.name, id: s.id, coords: s.coords, depTime: s.departure, arrTime: s.arrival })),
                    { name: transitLeg.destination.name, id: transitLeg.destination.id, coords: transitLeg.destination.coords, arrTime: transitLeg.destination.arrival },
                  ].filter((s, i, arr) => i === 0 || s.id !== arr[0].id).filter((s, i, arr) => i === arr.length - 1 || s.id !== arr[arr.length - 1].id || i === arr.length - 1);
                  setRouteStops(allStops);
                  // Use the actual path polyline for the route line if available
                  if (transitLeg.path && transitLeg.path.length >= 2) {
                    setLegPath(transitLeg.path);
                  }

                  // Backfill missing coords by ID lookup
                  const missing = allStops.filter(s => !s.coords && s.id);
                  if (missing.length > 0) {
                    const backfills = await Promise.all(
                      missing.map(async (s) => {
                        try {
                          const sr = await Promise.race([
                            searchStops(s.id),
                            new Promise<never>((_, rej) => setTimeout(() => rej(new Error('timeout')), 2000)),
                          ]);
                          if (sr[0]?.latitude && sr[0]?.longitude) {
                            return { id: s.id, coords: [sr[0].longitude, sr[0].latitude] as [number, number] };
                          }
                        } catch { /* timeout or lookup failed */ }
                        return null;
                      }),
                    );
                    let changed = false;
                    const updated = [...allStops];
                    for (const bf of backfills) {
                      if (bf) {
                        const idx = updated.findIndex(s => s.id === bf.id);
                        if (idx >= 0) { updated[idx] = { ...updated[idx], coords: bf.coords }; changed = true; }
                      }
                    }
                    if (changed) setRouteStops(updated);
                  }
                  break; // Found a usable leg — stop trying candidates
                }
                // If we found a destStop but no useful transitLeg, try next candidate
                console.warn('RouteMap: leg found but no stops — trying next destination candidate');
              }
            }
          } catch (e) { console.warn('RouteMap: trip planner fallback chain failed:', e); }
        }
        }
      } catch (e) { console.warn('RouteMap: data loading failed:', e); }
      finally { setLoading(false); }
    })();
  }, [stopId, tripId, routeParam, timeParam, destinationParam]);

  // Load live vehicles — filter by route (not tripId, which is a different ID namespace)
  const loadVehicles = useCallback(async () => {
    if (!rtMode || !canPoll()) return;
    try {
      const positions = await fetchVehiclePositions(rtMode);
      const filtered = routeParam
        ? positions.filter(v => routeIdMatches(v.route_id, routeParam))
        : positions;
      // The Sydney Trains feed omits bearing — infer direction from the route
      // shape so markers still show which way each service is travelling.
      const needBearing = filtered.filter(v => !Number.isFinite(v.bearing));
      let withBearing = filtered;
      if (needBearing.length) {
        const inferred = await Promise.all(needBearing.map(async v => {
          const b = await inferVehicleBearing(v.route_id, [v.longitude, v.latitude]).catch(() => undefined);
          return b != null ? { ...v, bearing: b } : v;
        }));
        const byKey = new globalThis.Map(inferred.map(v => [v.vehicle_id || v.trip_id, v]));
        withBearing = filtered.map(v => byKey.get(v.vehicle_id || v.trip_id) || v);
      }
      setVehicles(withBearing);
      setLastVehicleUpdate(new Date());
    } catch { /* silent — vehicle overlay is optional */ }
  }, [rtMode, routeParam]);

  const selectRouteVehicle = useCallback(async (vehicle: RouteVehicle) => {
    setSelectedVehicle(vehicle);
    if (!rtMode || vehicle.delayLoaded) return;
    const delay = await fetchTripDelays(rtMode).then(delays => delays.get(vehicle.trip_id)).catch(() => undefined);
    const enriched = { ...vehicle, delay, delayLoaded: true };
    setSelectedVehicle(current => current === vehicle ? enriched : current);
    setVehicles(current => current.map(item => item === vehicle ? enriched : item));
  }, [rtMode]);

  useEffect(() => {
    if (!rtMode) { setVehicles([]); return; }
    void loadVehicles();
    vehicleIntervalRef.current = setInterval(() => { void loadVehicles(); }, 30000);
    const resume = () => {
      if (canPoll()) void loadVehicles();
    };
    document.addEventListener('visibilitychange', resume);
    window.addEventListener('online', resume);
    return () => {
      if (vehicleIntervalRef.current) clearInterval(vehicleIntervalRef.current);
      document.removeEventListener('visibilitychange', resume);
      window.removeEventListener('online', resume);
    };
  }, [loadVehicles, rtMode]);

  // Build route line GeoJSON — use leg path if available (follows actual roads/tracks)
  const coordsWithValues = routeStops.filter(s => s.coords);
  const lineCoords = legPath && legPath.length >= 2 ? legPath : coordsWithValues.map(s => s.coords!);
  const routeLine = lineCoords.length >= 2 ? {
    type: 'FeatureCollection' as const,
    features: [{ type: 'Feature' as const, properties: {}, geometry: { type: 'LineString' as const, coordinates: lineCoords } }],
  } : undefined;

  useEffect(() => {
    if (loading || !mapRef.current) return;
    // Entering from a stop/station should preserve that context instead of
    // immediately zooming back out to the bounds of the entire route.
    const requestedStop = routeStops.find(stop => stop.id === stopId && stop.coords);
    const focus = requestedStop?.coords || mapCenter;
    mapRef.current.flyTo({ center: focus, zoom: 16, duration: 500 });
  }, [loading, mapCenter, routeStops.length, stopId]);

  const currentRouteStopId = dep?.serviceStops?.[0]?.id || stopId;
  const currentStopIdx = routeStops.findIndex(s => s.id === currentRouteStopId);

  useEffect(() => {
    if (!loading && currentStopIdx >= 0) currentStopRowRef.current?.scrollIntoView({ block: 'center' });
  }, [loading, currentStopIdx]);

  useEffect(() => {
    if (selectedStop) stopRowRefs.current[selectedStop.id]?.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }, [selectedStop]);

  return (
    <main className="page-container">
      <button className="back-button" onClick={() => navigate(-1)}>← Back</button>
      <h1 className="page-title">{dep ? `${dep.routeNumber} to ${dep.destination}` : 'Route Map'}</h1>
      <p className="stop-id">{dep?.mode} • {dep?.platform ? `Platform ${dep.platform}` : ''} {!routeStops.length ? '• Route shown is approximate' : ''}</p>

      <div className="route-map-refresh"><span>Live vehicles refresh automatically</span><RefreshCountdown lastUpdate={lastVehicleUpdate} loading={loading} /></div>

      <div className="map-container" style={{ height: 'calc(45vh)', borderRadius: 10, overflow: 'hidden', marginBottom: 12, position: 'relative' }}>
        <Map
          ref={mapRef}
          initialViewState={{ longitude: mapCenter[0], latitude: mapCenter[1], zoom: 14 }}
          mapStyle={DARK_MAP_STYLE}
          style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
          attributionControl={false}
        >
          <NavigationControl position="top-right" />

          {/* Route line */}
          {routeLine && (
            <Source id="route-line" type="geojson" data={routeLine}>
              <Layer id="route-line-layer" type="line" paint={{ 'line-color': routeColor, 'line-width': 4, 'line-opacity': 0.6 }} />
            </Source>
          )}

          {/* Stop markers */}
          {routeStops.filter(s => s.coords).map((s) => (
            <Marker key={s.id || s.name} latitude={s.coords![1]} longitude={s.coords![0]} style={{ zIndex: 10 }} onClick={(e) => {
              e.originalEvent.stopPropagation();
              setSelectedStop({ id: s.id, name: s.name, coords: s.coords! });
            }}>
              <div
                className={s.id === currentRouteStopId ? 'stop-marker-badge stop-marker-badge--current' : 'stop-marker-badge'}
                style={{ background: s.id === currentRouteStopId ? 'white' : routeColor, color: s.id === currentRouteStopId ? routeColor : 'white', outline: selectedStop?.id === s.id ? '3px solid #fff' : undefined }}
              >
                {BADGE_LETTER[dep?.mode || modeParam] || 'T'}
              </div>
            </Marker>
          ))}

          {selectedStop && (
            <Popup latitude={selectedStop.coords[1]} longitude={selectedStop.coords[0]} anchor="bottom" offset={20} onClose={() => setSelectedStop(null)}>
              <strong>{selectedStop.name}</strong>
            </Popup>
          )}

          {/* Live vehicle markers */}
          {vehicles.filter(v => v.latitude && v.longitude).map((v) => (
            <Marker
              key={`${modeParam}:${v.vehicle_id || v.trip_id || 'vehicle'}:${v.latitude}:${v.longitude}`}
              latitude={v.latitude}
              longitude={v.longitude}
              style={{ zIndex: 20 }}
              onClick={(e) => {
                e.originalEvent.stopPropagation();
                void selectRouteVehicle(v);
              }}
            >
              <VehicleMarker
                mode={dep?.mode || modeParam}
                route={dep?.routeShortName || routeParam}
                destination={dep?.destination || destinationParam || undefined}
                bearing={v.bearing}
                title={`${vehicleDisplayLabel(dep?.mode || modeParam, dep?.routeShortName || routeParam, dep?.destination || destinationParam || undefined)}${v.delay != null ? ` — ${formatDelayText(v.delay)}` : ''}`}
              />
            </Marker>
          ))}

          {/* Vehicle popup */}
          {selectedVehicle && (
            <Popup
              latitude={selectedVehicle.latitude}
              longitude={selectedVehicle.longitude}
              anchor="bottom"
              onClose={() => setSelectedVehicle(null)}
              offset={20}
            >
              <div className="vehicle-popup">
                <strong>{formatRouteLabel(dep?.mode || '', dep?.routeShortName, dep?.destination)}</strong>
                {!selectedVehicle.delayLoaded ? (
                  <p>Loading delay…</p>
                ) : selectedVehicle.delay != null ? (
                  <p className={formatDelay(selectedVehicle.delay).cls}>{formatDelay(selectedVehicle.delay).text}</p>
                ) : (
                  <p>No delay info</p>
                )}
              </div>
            </Popup>
          )}
        </Map>
        {!routeStops.some(s => s.coords) && !loading && <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', color: 'var(--color-text-secondary)', fontSize: 12, textAlign: 'center', pointerEvents: 'none' }}>Route detail not available.<br/>Try a different service.</div>}
      </div>

      {/* Stop list */}
      {routeStops.length > 0 && (
        <div className="card" style={{ maxHeight: '45vh', overflowY: 'auto' }}>
          <h3 style={{ fontSize: 14, marginBottom: 8 }}>Stops on this route</h3>
          {routeStops.map((s, i) => (
            <button ref={(node) => { stopRowRefs.current[s.id] = node; if (s.id === currentRouteStopId) currentStopRowRef.current = node; }} key={i} className="leg-stop-row" onClick={() => {
              if (s.coords) {
                setSelectedStop({ id: s.id, name: s.name, coords: s.coords });
                mapRef.current?.flyTo({ center: s.coords, zoom: 16, duration: 800 });
              }
            }} style={{ width: '100%', border: 0, cursor: s.coords ? 'pointer' : 'default', background: selectedStop?.id === s.id ? 'rgba(0,114,198,0.2)' : s.id === currentRouteStopId ? 'rgba(255,107,0,0.1)' : 'transparent', padding: '6px 8px', borderRadius: 4, marginBottom: 2 }}>
              <span className="leg-stop-name" style={{ fontWeight: s.id === currentRouteStopId ? 700 : 400 }}>{s.name}</span>
              <span className="leg-stop-time">{s.depTime ? new Date(s.depTime).toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' }) : s.arrTime ? new Date(s.arrTime).toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' }) : '—'}</span>
            </button>
          ))}
          {currentStopIdx >= 0 && <div style={{ fontSize: 11, color: 'var(--color-text-secondary)', marginTop: 8 }}>● Current stop</div>}
        </div>
      )}

      {loading && <div className="home-loading">Loading route...</div>}
    </main>
  );
}
